"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  LiveAttempt,
  LiveCompetitor,
  LiveResult,
  LiveRound,
  PendingLiveResult,
} from "@/types/live";
import useAPI from "@/lib/wca/useAPI";
import useResultsSubscriptions, {
  ConnectionState,
  DiffedLiveResult,
  DiffProtocolResponse,
} from "@/lib/hooks/useResultsSubscription";
import { applyDiffToLiveResults } from "@/lib/live/applyDiffToLiveResults";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import {
  decompressFullResult,
  decompressPartialResult,
} from "@/lib/live/decompressDiff";
import { countCompletedResults } from "@/lib/live/countCompletedResults";
import { useAllRoundsInfo } from "@/providers/RoundInfoProvider";
import { SERVER_SEEDED_STALE_TIME } from "@/providers/WCAQueryClientProvider";

export type LiveResultsByRegistrationId = Record<string, LiveResult[]>;
interface LiveResultContextType {
  liveResultsByRegistrationId: LiveResultsByRegistrationId;
  addPendingLiveResult: (
    liveResult: PendingLiveResult,
    roundWcifId: string,
  ) => void;
  pendingLiveResults: PendingLiveResult[];
  addPendingQuitCompetitor: (registrationId: number) => void;
  pendingQuitCompetitors: Set<number>;
  connectionState: ConnectionState;
  competitors: Map<number, LiveCompetitor>;
}

const LiveResultContext = createContext<LiveResultContextType | undefined>(
  undefined,
);

const compareAttempts = (
  attemptsA: LiveAttempt[],
  attemptsB: LiveAttempt[],
) => {
  const sortedA = attemptsA.toSorted(
    (a, b) => a.attempt_number - b.attempt_number,
  );
  const sortedB = attemptsB.toSorted(
    (a, b) => a.attempt_number - b.attempt_number,
  );

  return (
    sortedA.length === sortedB.length &&
    sortedA.every(
      (a, i) =>
        a.value === sortedB[i].value &&
        a.attempt_number === sortedB[i].attempt_number,
    )
  );
};

export function LiveResultProvider({
  initialRound,
  competitionId,
  children,
}: {
  initialRound: LiveRound;
  competitionId: string;
  children: ReactNode;
}) {
  return (
    <MultiRoundResultProvider
      initialRounds={[initialRound]}
      competitionId={competitionId}
    >
      {children}
    </MultiRoundResultProvider>
  );
}

export function MultiRoundResultProvider({
  initialRounds,
  competitionId,
  children,
}: {
  initialRounds: LiveRound[];
  competitionId: string;
  children: ReactNode;
}) {
  const [pendingResults, updatePendingResults] = useState<PendingLiveResult[]>(
    [],
  );
  const [pendingQuitCompetitors, updatePendingQuitCompetitors] = useState<
    Set<number>
  >(new Set());

  const api = useAPI();
  const queryClient = useQueryClient();
  const { setCompletedCount, setTotalCompetitors } = useAllRoundsInfo();

  const roundQueryOptions = useCallback(
    (roundId: string) => {
      return api.queryOptions(
        "get",
        "/v1/competitions/{competitionId}/live/rounds/{roundId}",
        {
          params: { path: { roundId, competitionId } },
        },
      );
    },
    [api, competitionId],
  );

  // One query per round
  const queries = initialRounds.map((round) => ({
    ...roundQueryOptions(round.id),
    initialData: round,
    refetchOnMount: true,
    staleTime: SERVER_SEEDED_STALE_TIME,
  }));

  const {
    liveResultsByRegistrationId,
    stateHashesByRoundId,
    competitors,
    refetchRound,
  } = useQueries({
    queries,
    combine: (queryResults) => ({
      liveResultsByRegistrationId: _.groupBy(
        queryResults.flatMap((r) => r.data.results),
        "registration_id",
      ),
      stateHashesByRoundId: Object.fromEntries(
        queryResults.map((r) => [r.data.id, r.data.state_hash]),
      ),
      competitors: new Map(
        queryResults.flatMap((r) => r.data.competitors.map((c) => [c.id, c])),
      ),
      refetchRound: async (roundId: string) => {
        return queryResults.find((r) => r.data.id === roundId)!.refetch();
      },
    }),
  });

  const diffPendingResults = useCallback(
    <T extends DiffedLiveResult>(
      incomingResults: T[],
      comparisonFn: (pending: PendingLiveResult, incoming: T) => boolean,
    ) => {
      updatePendingResults((prevPendingResults) =>
        prevPendingResults.filter(
          (pr) =>
            !incomingResults.some(
              (ir) =>
                ir.registration_id === pr.registration_id &&
                comparisonFn(pr, ir),
            ),
        ),
      );
    },
    [],
  );

  const onReceived = (roundId: string, diff: DiffProtocolResponse) => {
    const {
      updated = [],
      created = [],
      deleted = [],
      before_hash,
      after_hash,
    } = diff;

    if (before_hash !== stateHashesByRoundId[roundId]) {
      refetchRound(roundId).then((res) => {
        if (!res.isSuccess) {
          return;
        }

        const newData = res.data;
        const newResults = newData.results;
        const newCompetitors = newData.competitors;

        setCompletedCount(roundId, newData.completed_competitors);
        setTotalCompetitors(roundId, newCompetitors.length);

        // We just made a full refetch. Only keep those results as "pending"
        //   which are NOT contained exactly in the refetched round.
        // In other words, if we find a competitor with the updated attempts
        //   in the refetched round, then their result is not pending anymore.
        diffPendingResults(newResults, (pr, ir) =>
          compareAttempts(pr.attempts, ir.attempts),
        );

        updatePendingQuitCompetitors((currentlyQuitCompetitors) =>
          // Only keep pending quit markers if they are _still_ in the refetched round
          //   (ie the "quit" has not been executed yet)
          currentlyQuitCompetitors.intersection(
            new Set(newCompetitors.map((r) => r.id)),
          ),
        );
      });
    } else {
      const decompressedUpdated = updated.map(decompressPartialResult);
      const decompressedCreated = created.map(decompressFullResult);

      const deletedSet = new Set(deleted);
      const roundQuery = roundQueryOptions(roundId);

      queryClient.setQueryData(
        roundQuery.queryKey,
        (oldData: LiveRound): LiveRound => ({
          ...oldData,
          results: applyDiffToLiveResults({
            previousResults: oldData.results,
            updated: decompressedUpdated,
            created: decompressedCreated,
            deleted,
            roundWcifId: roundId,
          }),
          state_hash: after_hash,
          competitors: [
            ...oldData.competitors,
            ...created.map((c) => c.user),
          ].filter((c) => !deletedSet.has(c.id)),
        }),
      );

      const newRound = queryClient.getQueryData<LiveRound>(roundQuery.queryKey);
      if (newRound) {
        setCompletedCount(roundId, countCompletedResults(newRound));
        setTotalCompetitors(roundId, newRound.results.length);
      }

      diffPendingResults(decompressedUpdated, (pr, ir) => {
        // The incoming values are diffs, meaning (type-wise)
        //   they might not actually contain attempts. For example when only advancing is updated
        return (
          ir.attempts !== undefined && compareAttempts(pr.attempts, ir.attempts)
        );
      });

      updatePendingQuitCompetitors((currentlyQuitCompetitors) =>
        // If a competitor is listed as "deleted", then consider that our pending quit was executed by the backend
        currentlyQuitCompetitors.difference(new Set(deleted)),
      );
    }
  };

  const addPendingLiveResult = useCallback(
    (liveResult: PendingLiveResult, roundWcifId: string) => {
      updatePendingResults((pending) => [
        ...pending,
        ...applyDiffToLiveResults({
          previousResults:
            liveResultsByRegistrationId[liveResult.registration_id],
          updated: [liveResult],
          roundWcifId: roundWcifId,
        }),
      ]);
    },
    [liveResultsByRegistrationId],
  );

  const addPendingQuitCompetitor = useCallback((registrationId: number) => {
    updatePendingQuitCompetitors((currentlyQuitCompetitors) =>
      currentlyQuitCompetitors.add(registrationId),
    );
  }, []);

  const refetchAndClearPending = (roundId: string) => {
    refetchRound(roundId).then((res) => {
      if (!res.isSuccess) {
        return;
      }
      diffPendingResults(res.data.results, (pr, ir) =>
        compareAttempts(pr.attempts, ir.attempts),
      );
    });
  };

  const roundIds = initialRounds.map((r) => r.id);
  const connectionState = useResultsSubscriptions(
    roundIds,
    competitionId,
    onReceived,
    refetchAndClearPending,
  );

  return (
    <LiveResultContext.Provider
      value={{
        liveResultsByRegistrationId,
        pendingLiveResults: pendingResults,
        addPendingLiveResult,
        pendingQuitCompetitors,
        addPendingQuitCompetitor,
        connectionState,
        competitors,
      }}
    >
      {children}
    </LiveResultContext.Provider>
  );
}

export function useLiveResults() {
  const context = useContext(LiveResultContext);
  if (context === undefined) {
    throw new Error("useLiveResults must be used within a LiveResultProvider");
  }
  return context;
}
